export type WidgetTypeOption = "ticker" | "bar" | "table" | "pie" | "line" | "funnel" | "bubble" | "stacked-bar-line" | "custom";

// Widget types that don't support data filters by design: ticker is a single-value KPI,
// funnel needs the full conversion sequence, bubble's three-axis layout breaks if rows drop.
export const WIDGET_TYPES_WITHOUT_FILTERS: readonly WidgetTypeOption[] = ["ticker", "funnel", "bubble"] as const;

export const WIDGET_TYPE_LABELS: Record<WidgetTypeOption, string> = {
    ticker: "Ticker",
    bar: "Bar Chart",
    table: "Table",
    pie: "Pie Chart",
    line: "Line Chart",
    funnel: "Funnel",
    bubble: "Bubble Chart",
    "stacked-bar-line": "Combo Chart",
    custom: "Custom",
};

export interface WidgetTypeConfig {
    label: string;
    value: WidgetTypeOption;
    description: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

export interface ProductSelectorProps {
    selectedIds: number[];
    products: Array<{ id: number; name: string; serviceName?: string }>;
    onChange: (ids: number[]) => void;
    isLoading?: boolean;
    required?: boolean | { required: boolean; message: string };
}

export interface LevelSelectorProps {
    selectedId: number | null;
    levels: Array<{ id: number; displayName: string }>;
    onChange: (id: number | null) => void;
    emptyMessage?: string;
    isLoading?: boolean;
    required?: boolean | { required: boolean; message: string };
}

export interface FilterBySelectorProps {
    selectedIds: number[];
    filters: Array<{ id: number; name: string }>;
    onChange: (ids: number[]) => void;
    emptyMessage?: string;
    isLoading?: boolean;
    required?: boolean | { required: boolean; message: string };
}

export interface SegmentSelectorProps {
    selectedId: number | null;
    segments: Array<{ id: number; displayName: string }>;
    onChange: (id: number | null) => void;
    emptyMessage?: string;
    isLoading?: boolean;
}

export interface MetricSelectorProps {
    selectedIds: number[];
    metrics: Array<{ id: number; name: string; showName: string; category?: string }>;
    onChange: (ids: number[]) => void;
    emptyMessage?: string;
    isLoading?: boolean;
    required?: boolean | { required: boolean; message: string };
    /** Metric IDs marked as hidden-by-default ("dynamic"). Enables the per-chip popover when paired with `onToggleDynamic`. */
    dynamicIds?: number[];
    /** Toggles a metric's hidden-by-default ("dynamic") state. */
    onToggleDynamic?: (id: number) => void;
}

export interface WidgetTypeSelectorProps {
    selectedType: WidgetTypeOption | null;
    options: WidgetTypeConfig[];
    enabledTypes: Record<WidgetTypeOption, boolean>;
    onChange: (type: WidgetTypeOption) => void;
    blockedMessage?: string;
}

export interface SegmentOptionsToggleProps {
    groupBySegment: boolean;
    onGroupBySegmentChange: (value: boolean) => void;
}

export type DatePreset =
    | "yesterday"
    | "last_7_days"
    | "last_14_days"
    | "last_30_days"
    | "last_90_days"
    | "last_12_months"
    | "this_month"
    | "last_month"
    | "this_year"
    | "last_year"
    | "custom";

export interface DateSelectorProps {
    usePageDate: boolean;
    onToggleUsePageDate: (value: boolean) => void;
    preset: DatePreset;
    onPresetChange: (value: DatePreset) => void;
    customRange?: { from: Date | undefined; to: Date | undefined };
    onCustomRangeChange: (range: { from: Date | undefined; to: Date | undefined }) => void;
}
